import React, { useState, useEffect } from 'react';
import { collectorAPI } from '../services/api';
import { CollectionStatusLabels, UrgencyLevelLabels, WasteTypeLabels } from '../types/collection';
import './CollectorTasksPage.css';

interface AssignedRequest {
  id: number;
  waste_type: string;
  description: string;
  collection_address: string;
  collection_date?: string;
  urgency: string;
  status: string;
  estimated_weight?: number;
  estimated_volume?: number;
  actual_volume?: number;
  contact_phone?: string;
  special_instructions?: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  assigned_collector_id?: number;
}

const CollectorTasksPage: React.FC = () => {
  const [requests, setRequests] = useState<AssignedRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedRequest, setSelectedRequest] = useState<AssignedRequest | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [actualVolume, setActualVolume] = useState<number | ''>('');
  const [isUpdatingVolume, setIsUpdatingVolume] = useState(false);
  const [showVolumeUpdate, setShowVolumeUpdate] = useState<number | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadMyRequests();
  }, [statusFilter]);

  const loadMyRequests = async () => {
    try {
      setLoading(true);
      const data = await collectorAPI.getMyRequests(statusFilter || undefined);
      setRequests(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || '배정된 작업을 불러오는 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRequest = async (requestId: number) => {
    try {
      const response = await collectorAPI.startRequest(requestId);
      alert('작업이 시작되었습니다');
      loadMyRequests(); // 목록 새로고침
    } catch (err: any) {
      setError(err.response?.data?.detail || '작업 시작 중 오류가 발생했습니다');
    }
  };

  const handleCompleteRequest = async (requestId: number) => {
    if (selectedPhotos.length === 0) {
      setError('완료 증빙 사진을 최소 1장 업로드해야 합니다');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      selectedPhotos.forEach((photo, index) => {
        formData.append('photos', photo);
      });
      
      // 용량은 선택사항으로 처리
      console.log('DEBUG: actualVolume value:', actualVolume);
      console.log('DEBUG: actualVolume type:', typeof actualVolume);
      console.log('DEBUG: actualVolume truthy check:', !!actualVolume);
      
      if (actualVolume) {
        console.log('DEBUG: Converting actualVolume to string:', actualVolume.toString());
        formData.append('actual_volume', actualVolume.toString());
      } else {
        console.log('DEBUG: No actual_volume provided - value is falsy');
      }
      
      // FormData 내용 로깅
      console.log('DEBUG: FormData entries:');
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        console.log('DEBUG:', key, '=', value);
      });
      if (completionNotes) {
        formData.append('completion_notes', completionNotes);
      }

              const response = await fetch(`http://34.64.95.133:8002/api/collectors/me/requests/${requestId}/complete-with-photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        let message = `작업이 완료되었습니다. ${result.photos_uploaded}장의 사진이 업로드되었습니다.`;
        if (result.volume_required) {
          message += ' 나중에 용량 정보를 추가로 입력할 수 있습니다.';
        }
        alert(message);
        setSelectedRequest(null);
        setCompletionNotes('');
        setActualVolume('');
        setSelectedPhotos([]);
        loadMyRequests();
      } else {
        const error = await response.json();
        setError(error.detail || '작업 완료 처리 중 오류가 발생했습니다');
      }
    } catch (err: any) {
      console.error('Failed to complete request:', err);
      setError('작업 완료 처리 중 오류가 발생했습니다');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateVolume = async (requestId: number) => {
    if (!actualVolume) {
      setError('용량을 입력해주세요');
      return;
    }

    console.log('Updating volume for request', requestId, 'with volume:', actualVolume);

    try {
      setIsUpdatingVolume(true);
      const response = await collectorAPI.updateVolume(requestId, Number(actualVolume));
      console.log('Volume update response:', response);
      alert('용량 정보가 성공적으로 업데이트되었습니다');
      setShowVolumeUpdate(null);
      setActualVolume('');
      loadMyRequests();
    } catch (err: any) {
      console.error('Volume update error:', err);
      setError(err.response?.data?.detail || '용량 업데이트 중 오류가 발생했습니다');
    } finally {
      setIsUpdatingVolume(false);
    }
  };

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      // 최대 5장까지 제한
      if (files.length > 5) {
        setError('최대 5장까지만 업로드할 수 있습니다');
        return;
      }
      setSelectedPhotos(files);
      setError(''); // 에러 메시지 초기화
    }
  };

  const removePhoto = (index: number) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#fbbf24';
      case 'confirmed': return '#3b82f6';
      case 'in_progress': return '#8b5cf6';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      case 'urgent': return '#dc2626';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="collector-tasks-page">
        <div className="loading">배정된 작업을 로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="collector-tasks-page">
      <div className="page-header">
        <h1>배정된 작업</h1>
        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-label">전체 작업</span>
            <span className="stat-value">{requests.length}건</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">진행 중</span>
            <span className="stat-value">
              {requests.filter(r => r.status === 'in_progress').length}건
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">완료됨</span>
            <span className="stat-value">
              {requests.filter(r => r.status === 'completed').length}건
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')} className="close-error">×</button>
        </div>
      )}

      {/* 필터 */}
      <div className="filters">
        <div className="filter-group">
          <label>상태 필터:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">전체</option>
            <option value="assigned">배정됨</option>
            <option value="confirmed">확인됨</option>
            <option value="in_progress">진행 중</option>
            <option value="completed">완료됨</option>
          </select>
        </div>
      </div>

      {/* 작업 목록 */}
      <div className="tasks-section">
        {requests.length === 0 ? (
          <div className="no-tasks">
            <div className="no-tasks-icon">📋</div>
            <h3>배정된 작업이 없습니다</h3>
            <p>새로운 작업이 배정되면 여기에 표시됩니다.</p>
          </div>
        ) : (
          <div className="tasks-grid">
            {requests.map((request) => (
              <div key={request.id} className="task-card">
                <div className="task-header">
                  <div className="task-id">작업 #{request.id}</div>
                  <div className="task-badges">
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(request.status) }}
                    >
                      {CollectionStatusLabels[request.status as keyof typeof CollectionStatusLabels] || request.status}
                    </span>
                    <span 
                      className="urgency-badge"
                      style={{ backgroundColor: getUrgencyColor(request.urgency) }}
                    >
                      {UrgencyLevelLabels[request.urgency as keyof typeof UrgencyLevelLabels] || request.urgency}
                    </span>
                  </div>
                </div>

                <div className="task-content">
                  <div className="waste-type">
                    <strong>폐기물 유형:</strong> {WasteTypeLabels[request.waste_type as keyof typeof WasteTypeLabels] || request.waste_type}
                  </div>
                  
                  <div className="address">
                    <strong>📍 수거 주소:</strong> {request.collection_address}
                  </div>
                  
                  <div className="description">
                    <strong>상세 설명:</strong> {request.description}
                  </div>

                  {request.collection_date && (
                    <div className="collection-date">
                      <strong>🕒 희망 수거일:</strong> {formatDate(request.collection_date)}
                    </div>
                  )}

                  {request.contact_phone && (
                    <div className="contact">
                      <strong>📞 연락처:</strong> {request.contact_phone}
                    </div>
                  )}

                  {request.estimated_volume && (
                    <div className="estimated-volume">
                      <strong>📏 예상 용량:</strong> {request.estimated_volume}L
                    </div>
                  )}
                  
                  {request.actual_volume && (
                    <div className="actual-volume">
                      <strong>📐 실제 용량:</strong> {request.actual_volume}L
                    </div>
                  )}

                  {request.special_instructions && (
                    <div className="special-instructions">
                      <strong>📝 특별 지시사항:</strong> {request.special_instructions}
                    </div>
                  )}

                  <div className="created-date">
                    <strong>생성일:</strong> {formatDate(request.created_at)}
                  </div>
                </div>

                <div className="task-actions">
                  {(request.status === 'confirmed' || request.status === 'assigned') && (
                    <button 
                      className="start-btn"
                      onClick={() => handleStartRequest(request.id)}
                    >
                      작업 시작
                    </button>
                  )}
                  
                  {request.status === 'in_progress' && (
                    <button 
                      className="complete-btn"
                      onClick={() => setSelectedRequest(request)}
                    >
                      작업 완료
                    </button>
                  )}

                  {request.status === 'completed' && !request.actual_volume && (
                    <button 
                      className="update-volume-btn"
                      onClick={() => setShowVolumeUpdate(request.id)}
                    >
                      용량 입력
                    </button>
                  )}

                  <button 
                    className="details-btn"
                    onClick={() => setSelectedRequest(request)}
                  >
                    상세보기
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 작업 완료 모달 */}
      {selectedRequest && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>작업 완료 - #{selectedRequest.id}</h2>
              <button onClick={() => setSelectedRequest(null)} className="close-btn">×</button>
            </div>
            
            <div className="modal-content">
              <div className="task-summary">
                <h3>작업 요약</h3>
                <p><strong>주소:</strong> {selectedRequest.collection_address}</p>
                <p><strong>폐기물:</strong> {WasteTypeLabels[selectedRequest.waste_type as keyof typeof WasteTypeLabels]}</p>
                <p><strong>예상 용량:</strong> {selectedRequest.estimated_volume || '미지정'}L</p>
              </div>

              {selectedRequest.status === 'in_progress' && (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleCompleteRequest(selectedRequest.id);
                }} className="completion-form">
                  <div className="form-group">
                    <label>완료 증빙 사진 *</label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handlePhotoSelect}
                      className="photo-input"
                    />
                    <div className="photo-help">
                      최대 5장까지 업로드 가능합니다. 수거 전후 사진을 업로드해주세요.
                    </div>
                    
                    {selectedPhotos.length > 0 && (
                      <div className="photo-preview">
                        <h4>선택된 사진 ({selectedPhotos.length}장)</h4>
                        <div className="photo-list">
                          {selectedPhotos.map((photo, index) => (
                            <div key={index} className="photo-item">
                              <img 
                                src={URL.createObjectURL(photo)} 
                                alt={`Preview ${index + 1}`}
                                className="photo-thumbnail"
                              />
                              <button 
                                type="button"
                                onClick={() => removePhoto(index)}
                                className="remove-photo-btn"
                              >
                                ×
                              </button>
                              <div className="photo-name">{photo.name}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>실제 수거 용량 (L) - 선택사항</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={actualVolume}
                      onChange={(e) => setActualVolume(e.target.value ? parseFloat(e.target.value) : '')}
                      placeholder="실제로 수거한 용량을 리터(L) 단위로 입력하세요"
                    />
                    <div className="form-help">
                      용량 정보는 지금 입력하지 않아도 나중에 추가할 수 있습니다.
                    </div>
                  </div>

                  <div className="form-group">
                    <label>완료 메모</label>
                    <textarea
                      value={completionNotes}
                      onChange={(e) => setCompletionNotes(e.target.value)}
                      placeholder="작업 완료에 대한 메모를 입력하세요 (선택사항)"
                      rows={4}
                      maxLength={1000}
                    />
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={() => {
                      setSelectedRequest(null);
                      setSelectedPhotos([]);
                      setCompletionNotes('');
                      setActualVolume('');
                    }} className="cancel-btn">
                      취소
                    </button>
                    <button 
                      type="submit" 
                      className="complete-btn"
                      disabled={uploading || selectedPhotos.length === 0}
                    >
                      {uploading ? '업로드 중...' : '작업 완료 확정'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 용량 업데이트 모달 */}
      {showVolumeUpdate && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>용량 정보 입력 - 작업 #{showVolumeUpdate}</h2>
              <button onClick={() => {
                setShowVolumeUpdate(null);
                setActualVolume('');
              }} className="close-btn">×</button>
            </div>
            
            <div className="modal-content">
              <div className="volume-update-form">
                <p>완료된 작업의 실제 수거 용량을 입력해주세요.</p>
                
                <div className="form-group">
                  <label>실제 수거 용량 (L) *</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={actualVolume}
                    onChange={(e) => setActualVolume(e.target.value ? parseFloat(e.target.value) : '')}
                    placeholder="실제로 수거한 용량을 리터(L) 단위로 입력하세요"
                    required
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowVolumeUpdate(null);
                      setActualVolume('');
                    }} 
                    className="cancel-btn"
                  >
                    취소
                  </button>
                  <button 
                    type="button"
                    onClick={() => handleUpdateVolume(showVolumeUpdate)}
                    className="complete-btn"
                    disabled={isUpdatingVolume || !actualVolume}
                  >
                    {isUpdatingVolume ? '업데이트 중...' : '용량 등록'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectorTasksPage;